import React, { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { useProjectRealtime } from '../lib/useRealtime';
import { usePermission } from '../lib/permissions';
import { Send, Paperclip, Trash2, Download, FileText, X } from 'lucide-react';
import { Avatar } from '../pages/AppLayout';

export default function ChatWidget({ channelId, title = "Chat" }) {
  const { user } = useAuth();
  const { has } = usePermission();
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [attachment, setAttachment] = useState(null);

  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Load chat history
  const loadMessages = async () => {
    if (!channelId) return;
    try {
      const { data } = await api.get(`/chat/channels/${channelId}/messages`);
      setMessages(data.messages);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (e) {
      console.error('Error loading widget messages', e);
    }
  };

  useEffect(() => {
    loadMessages();
  }, [channelId]);

  // Real-time subscribe
  useProjectRealtime(channelId ? `chat:${channelId}` : null, (data) => {
    if (data.event === 'chat_message') {
      setMessages(prev => {
        if (prev.some(m => m.id === data.message.id)) return prev;
        return [...prev, data.message];
      });
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } else if (data.event === 'chat_message_deleted') {
      setMessages(prev => prev.filter(m => m.id !== data.message_id));
    }
  });

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputText.trim() && !attachment) return;

    try {
      const payload = { message_text: inputText };
      if (attachment) {
        payload.attachment_url = attachment.url;
        payload.attachment_name = attachment.name;
        payload.attachment_size = attachment.size;
      }

      const { data } = await api.post(`/chat/channels/${channelId}/messages`, payload);
      setMessages(prev => {
        if (prev.some(m => m.id === data.message.id)) return prev;
        return [...prev, data.message];
      });
      setInputText('');
      setAttachment(null);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch (err) {
      console.error('Error sending widget message', err);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const { data } = await api.post('/chat/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setAttachment(data);
    } catch (err) {
      alert(err.response?.data?.error || 'File upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteMessage = async (msgId) => {
    if (!confirm('Delete this message?')) return;
    try {
      await api.delete(`/chat/messages/${msgId}`);
      setMessages(prev => prev.filter(m => m.id !== msgId));
    } catch (err) {
      console.error('Failed to delete message', err);
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  return (
    <div className="flex flex-col h-full bg-slate-50 border-l border-slate-200 w-80 shadow-md">
      <div className="p-3 border-b bg-white flex justify-between items-center">
        <span className="font-bold text-sm text-slate-800 flex items-center gap-1.5">💬 {title}</span>
      </div>

      {/* Message Feed */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map(msg => {
          const isMe = msg.sender_id === user.id;
          return (
            <div key={msg.id} className={`flex items-start gap-2 max-w-[90%] ${isMe ? 'ml-auto flex-row-reverse' : ''}`}>
              <Avatar user={{ full_name: msg.sender_name, avatar_color: msg.sender_color }} size={24} />
              <div className="flex flex-col">
                <div className={`flex items-center gap-1 text-[10px] text-slate-500 mb-0.5 ${isMe ? 'justify-end' : ''}`}>
                  <span className="font-bold truncate max-w-[80px]">{msg.sender_name}</span>
                  <span>{new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  {(isMe || has('chat.delete_messages')) && (
                    <button
                      onClick={() => handleDeleteMessage(msg.id)}
                      className="p-0.5 rounded text-red-500 opacity-60 hover:opacity-100 transition-opacity"
                    >
                      <Trash2 size={10} />
                    </button>
                  )}
                </div>

                <div className={`p-2 rounded text-xs shadow-sm ${isMe ? 'bg-blue-600 text-white rounded-tr-none' : 'bg-white text-slate-800 rounded-tl-none border border-slate-100'}`}>
                  {msg.message_text && <p className="whitespace-pre-wrap">{msg.message_text}</p>}

                  {msg.attachment_url && (
                    <div className={`mt-1.5 p-1 rounded flex items-center gap-2 border text-[10px] ${isMe ? 'bg-blue-700 border-blue-500 text-white' : 'bg-slate-50 border-slate-200 text-slate-800'}`}>
                      {msg.attachment_url.match(/\.(jpeg|jpg|gif|png|webp)/i) ? (
                        <div className="flex flex-col gap-1">
                          <img
                            src={msg.attachment_url}
                            alt={msg.attachment_name}
                            className="max-w-[150px] max-h-[100px] rounded object-cover cursor-pointer"
                            onClick={() => window.open(msg.attachment_url, '_blank')}
                          />
                        </div>
                      ) : (
                        <>
                          <FileText size={14} className={isMe ? 'text-white' : 'text-blue-600'} />
                          <span className="truncate max-w-[100px]">{msg.attachment_name}</span>
                          <a
                            href={msg.attachment_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-inherit hover:underline"
                          >
                            <Download size={10} />
                          </a>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-2.5 border-t bg-white">
        <form onSubmit={handleSendMessage} className="flex flex-col gap-1.5">
          {attachment && (
            <div className="p-1 bg-slate-50 border rounded flex items-center justify-between text-[10px] text-slate-600">
              <span className="truncate max-w-[140px] font-semibold">{attachment.name}</span>
              <button type="button" onClick={() => setAttachment(null)} className="text-slate-500 hover:text-slate-800">
                <X size={10} />
              </button>
            </div>
          )}
          <div className="flex items-center gap-2">
            {has('chat.share_file') && (
              <>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="p-1.5 rounded hover:bg-slate-100 text-slate-500 border"
                >
                  {uploading ? (
                    <div className="w-3.5 h-3.5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Paperclip size={12} />
                  )}
                </button>
                <input type="file" ref={fileInputRef} onChange={handleFileUpload} className="hidden" />
              </>
            )}
            <input
              type="text"
              value={inputText}
              onChange={e => setInputText(e.target.value)}
              placeholder="Message..."
              className="flex-1 px-3 py-1.5 border rounded text-xs outline-none focus:border-blue-500"
            />
            <button
              type="submit"
              disabled={!inputText.trim() && !attachment}
              className="p-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              <Send size={12} />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
